/**
 * TimelineCanvas — the Plan page's Timeline panel, specializing the generic GridCanvas engine
 * (see GridCanvas.tsx for the actual pan/zoom/frozen-pane mechanics) to render placed
 * PlanBoardCards. This file owns everything Plan-specific: the PlanBoardItem/PlanCardPlacement
 * data model, the Timeline grid's specific geometry (16 columns, frozen 5-minor-cell
 * row/column), and the PlanBoardCard rendering itself — GridCanvas knows about none of that,
 * only generic `{x,y,width,height}` placements and a `renderItem` callback. The visual theme
 * (background, grid lines vs. dots, grid color) is centralized in theme.ts's
 * timelineCanvasThemes/activeTimelineCanvasTheme, not owned by this file.
 *
 * Card placement/drag/resize state is owned by PlanPage (see PlanPage.tsx) — this component is a
 * controlled renderer plus an imperative coordinate-math API (see TimelineCanvasHandle, a type
 * alias for GridCanvas's own GridCanvasHandle) that PlanPage uses to convert between screen and
 * canvas space for its drag state machine.
 */
import { forwardRef, useMemo } from 'react';
import GridCanvas, {
    GridCanvasHandle,
    CanvasRect,
    DrawnLine,
    CanvasConnector,
    CanvasConnectorPreview,
    ScreenRect,
    clampGridPanX,
    clampGridPanY,
    snapToGridSpacing,
} from './GridCanvas';
import PlanBoardCard, { ResizeEdge } from '../pages/PlanBoardCard';
import PlanAxisLabelCell from '../pages/PlanAxisLabelCell';
import { PlanBoardItem, PlanCardPlacement, PlanDrawnLine, PlanAxisLabel } from 'types';
import { computeRegions, getRegionForPlacement } from 'utils/planRegions';
import { findAxisLabel } from 'utils/planAxisLabels';
import { timelineCanvasThemes, activeTimelineCanvasTheme } from 'theme';

/** Side length, in canvas units, of one major grid cell. */
export const TIMELINE_GRID_SPACING = 40;
/** Number of minor divisions along each side of a major grid cell. */
export const TIMELINE_MINOR_DIVISIONS = 5;
/** Side length, in canvas units, of one minor grid cell — the snapping unit. */
export const TIMELINE_MINOR_SPACING = TIMELINE_GRID_SPACING / TIMELINE_MINOR_DIVISIONS;
/** Number of major columns the grid spans horizontally — its fixed, panable width. Doubled from
 * its original 16 so there's substantially more room to pan right; TIMELINE_VISIBLE_COLUMNS
 * (below) is unchanged, so the initial fit-to-width zoom/view looks identical — only how far
 * right panning can go grows. */
export const TIMELINE_HORIZONTAL_COLUMNS = 32;
/** Number of major columns shown at the mount-time fit zoom — the rest extend past the viewport and are
 * only reachable by panning right. Kept below TIMELINE_HORIZONTAL_COLUMNS so added columns actually widen
 * the pannable canvas instead of just shrinking every column to still fit the viewport exactly. */
const TIMELINE_VISIBLE_COLUMNS = 15;
/** Total horizontal extent of the grid, in canvas units. */
export const TIMELINE_GRID_WIDTH = TIMELINE_GRID_SPACING * TIMELINE_HORIZONTAL_COLUMNS;
/** Number of major rows the grid spans vertically — its fixed, pannable height, the vertical
 * mirror of TIMELINE_HORIZONTAL_COLUMNS. Deliberately overrides GridCanvas's own generic
 * `verticalExtent` default (DEFAULT_GRID_VERTICAL_EXTENT, 20000 canvas units — sized to be
 * "practically infinite" for a caller that never bounds panning past it): now that panning IS
 * bounded on every edge (see GridCanvas.tsx), a 20000-unit vertical extent would make the bottom
 * edge technically clamped but still many screens away at any normal zoom — reading as
 * unconstrained in practice. Set equal to TIMELINE_HORIZONTAL_COLUMNS so the grid's maximum
 * height matches its maximum width (a square pannable extent) rather than dwarfing it. */
const TIMELINE_VERTICAL_ROWS = TIMELINE_HORIZONTAL_COLUMNS;
/** Total vertical extent of the grid, in canvas units — TimelineCanvas's own `verticalExtent`
 * override passed to GridCanvas (see TIMELINE_VERTICAL_ROWS above for why the generic default
 * isn't used here). */
export const TIMELINE_GRID_HEIGHT = TIMELINE_GRID_SPACING * TIMELINE_VERTICAL_ROWS;
/** Zoom ceiling for wheel/trackpad zoom — GridCanvas's own default (3x the mount-time fit zoom)
 * reads as barely any zoom at all once a card's actual content needs inspecting up close, so
 * this raises the ceiling substantially. */
export const TIMELINE_MAX_ZOOM = 20;

/** Fixed on-screen size (CSS px) of the frozen row/column's reserved gap — Excel-freeze-pane
 * style, pinned to the viewport's left/top edge. Never scales with zoom and never animates open
 * or shut (there is no hover-collapse toggle anymore — see GridCanvas.tsx's own doc comment);
 * whenever a pane is shown, this is its exact size. A pane isn't shown at all, though, until it
 * has something to divide — see hasVerticalLine/hasHorizontalLine below, which zero out
 * frozenRowGapPx/frozenColumnGapPx on the GridCanvas call until a line of the matching
 * orientation exists.
 *
 * Sized well past the OLD hover-to-collapse strip (10px) it replaces — that collapsed size is
 * now the pane's ONLY size, since the expand/collapse affordance itself was removed — and then
 * some: a taller/wider gap gives the axis-label cells (PlanAxisLabelCell.tsx) more room, which is
 * what lets their own font-size actually read at a comfortable size rather than being squeezed
 * into a sliver. Deliberately independent from TIMELINE_FROZEN_COLUMN_WIDTH/
 * TIMELINE_FROZEN_ROW_HEIGHT below: this is a screen-pixel rendering size, those are a
 * canvas-unit reservation for the drop guard/region boundary — different units, different
 * purposes, same as before the collapse mechanic existed. */
export const TIMELINE_FROZEN_GAP_PX = 36;
/** Canvas-unit width reserved for the frozen column, used by PlanPage's
 * resolveFrozenAreaCandidate/resolveLinePosition as a default parameter (reached only when no
 * live TimelineCanvasHandle is available, e.g. direct unit-test calls — the real, live-zoom-aware
 * boundary always comes from TimelineCanvasHandle.getFrozenColumnWidth() instead) and as this
 * file's own Plan-region boundary (below) — 5 minor grid cells, unrelated to the frozen gap's own
 * fixed on-screen pixel size (TIMELINE_FROZEN_GAP_PX above). */
export const TIMELINE_FROZEN_COLUMN_WIDTH = TIMELINE_MINOR_SPACING * 5;
/** Same as TIMELINE_FROZEN_COLUMN_WIDTH, for the frozen row's height. */
export const TIMELINE_FROZEN_ROW_HEIGHT = TIMELINE_MINOR_SPACING * 5;

/** Active visual theme for this canvas — background, grid pattern (lines vs. dots), grid color,
 * and grid opacity all travel together (see timelineCanvasThemes in theme.ts, the centralized
 * place these are defined and swapped). */
const CANVAS_THEME = timelineCanvasThemes[activeTimelineCanvasTheme];

/** Default footprint (canvas units) a plan card is given the moment it's dropped on the grid — 9 minor cells wide, 6 tall
 * (2 minor cells taller than the card's actual content needs, so the title has room to spill onto its 2nd/3rd wrapped
 * line — see .plan-board-card--content-canvas .plan-board-card__title, app.css — without the fixed-height card clipping it). */
export const PLAN_CARD_DEFAULT_WIDTH = TIMELINE_MINOR_SPACING * 9;
export const PLAN_CARD_DEFAULT_HEIGHT = TIMELINE_MINOR_SPACING * 6;
/** Smallest a placed card's footprint can snap to (canvas units) — see GridCanvas's snapSize. */
export const PLAN_CARD_MIN_WIDTH = TIMELINE_MINOR_SPACING * 4;
export const PLAN_CARD_MIN_HEIGHT = TIMELINE_MINOR_SPACING * 3;

/** Stroke thickness, in constant on-screen CSS px (GridCanvas divides this by zoom internally,
 * so it never visually grows/shrinks as the user zooms — see GridCanvas.tsx's own doc comment),
 * for user-drawn vertical/horizontal lines — on par with the frozen divider's width (2, see the
 * `dividerWidth` passed to GridCanvas below) rather than noticeably heavier, while still reading
 * clearly above the faint grid lines around it. */
export const TIMELINE_DRAWN_LINE_THICKNESS = 2;
/** Default stroke color for a newly-drawn line — orange, chosen for contrast against the
 * blueprint-blue background and white grid/dividers (also the first of LINE_COLOR_PRESETS'
 * own orange swatch, PlanPage.tsx, so the picker's initial selection matches what's drawn). */
export const TIMELINE_DEFAULT_LINE_COLOR = '#f28c5c';

/**
 * Clamps horizontal pan so the Timeline grid's left/right edges never scroll past the viewport
 * edges — TimelineCanvas's own grid width bound to GridCanvas's generic clampGridPanX, including
 * its frozen-column-aware upper bound (TIMELINE_FROZEN_GAP_PX) so panned/zoomed content can never
 * end up dragged out from under the frozen column (see clampGridPanX's own doc comment).
 */
export function clampPanX(panX: number, zoomVal: number, viewportWidth: number): number {
    return clampGridPanX(panX, zoomVal, viewportWidth, TIMELINE_GRID_WIDTH, TIMELINE_FROZEN_GAP_PX);
}

/**
 * Clamps vertical pan so the Timeline grid's top/bottom edges never scroll past the viewport
 * edges — TimelineCanvas's own grid height (TIMELINE_GRID_HEIGHT) bound to GridCanvas's generic
 * clampGridPanY, including its frozen-row-aware upper bound, same as clampPanX above.
 */
export function clampPanY(panY: number, zoomVal: number, viewportHeight: number): number {
    return clampGridPanY(panY, zoomVal, viewportHeight, TIMELINE_GRID_HEIGHT, TIMELINE_FROZEN_GAP_PX);
}

/** Snaps a value to the Timeline grid's minor spacing — bound to GridCanvas's generic snapToGridSpacing. */
export function snapToMinorGrid(value: number): number {
    return snapToGridSpacing(value, TIMELINE_MINOR_SPACING);
}

/** Screen-space (viewport/fixed) rectangle, used for the drag-ghost/settle-animation math in PlanPage. */
export type { ScreenRect };

export type { ResizeEdge };

export type { CanvasConnector, CanvasConnectorPreview };

/** TimelineCanvasHandle is exactly GridCanvasHandle — TimelineCanvas forwards its ref straight
 * through to the underlying GridCanvas with no extra imperative surface of its own. */
export type TimelineCanvasHandle = GridCanvasHandle;

export interface TimelineCanvasProps {
    /** All plan board items, used to look up card content for placements by id. */
    items: PlanBoardItem[];
    /** Current placements (canvas units), keyed by PlanBoardItem id. */
    placements: Record<string, PlanCardPlacement>;
    /** Item id whose normal placed rendering should be suppressed (a ghost/settle animation represents it elsewhere). */
    hiddenPlacementId?: string | null;
    /** Item currently toggled selected (see PlanPage's selectedItemId) — shown with a highlight outline. */
    selectedItemId?: string | null;
    /** Called when the grid's empty background (not a card) is clicked — as opposed to
     * dragged/panned. Receives the native mouseup event so a caller can resolve the click's
     * canvas-space location (e.g. to clear the current selection, or place a drawn line). */
    onBackgroundClick?: (e: globalThis.MouseEvent) => void;
    /** Starts a move drag for an already-placed card. */
    onCardMouseDown: (e: React.MouseEvent, itemId: string) => void;
    /** Starts a resize drag from one of a placed card's edge-midpoint handles. */
    onResizeHandleMouseDown: (e: React.MouseEvent, itemId: string, edge: ResizeEdge) => void;
    /** True while a card dragged in from the Plan panel is hovering a spot the frozen-column
     * guard would reject (see PlanPage's resolveListDropCandidate) — swaps the cursor to
     * `not-allowed` so the rejection is obvious right under the pointer, not just on release. */
    dropForbidden?: boolean;
    /** True while a "click to place a line" tool is armed (see PlanPage's linePlacementMode) —
     * swaps the cursor to `crosshair`. */
    crosshair?: boolean;
    /** User-drawn vertical/horizontal lines placed on this grid. */
    lines?: DrawnLine[];
    /** Id of the currently-selected line, if any — rendered with a highlight halo. */
    selectedLineId?: string | null;
    /** Fired when a rendered line is pressed, e.g. to select it. */
    onLineMouseDown?: (lineId: string, e: React.MouseEvent<SVGLineElement>) => void;
    /** Ctrl+drag connections drawn between placed cards (see PlanBoardConnection, types.ts),
     * resolved into GridCanvas's generic CanvasConnector shape by PlanPage. */
    connectors?: CanvasConnector[];
    /** Live preview of a connector still being drawn — see PlanPage's useConnectDrag instance. */
    connectorPreview?: CanvasConnectorPreview | null;
    /** Item id currently acting as a Ctrl+drag connection's source — shown with a highlight
     * outline on its card (see PlanBoardCard's isConnectionSource). */
    connectSourceItemId?: string | null;
    /** Item id currently hovered as a Ctrl+drag connection's candidate target — shown with a
     * highlight outline on its card (see PlanBoardCard's isConnectionTarget). */
    connectTargetItemId?: string | null;
    /** Fired when a placed card is hovered while a Ctrl+drag connection is in progress — mirrors
     * CanvasNode's onConnectHover on the OST canvas. */
    onCardConnectHover?: (itemId: string) => void;
    /** Fired when a placed card stops being hovered during a Ctrl+drag connection. */
    onCardConnectLeave?: () => void;
    /** Fired by a selected Enabler card's delete-X button (see PlanBoardCard's onDelete) — no
     * other placed item type currently offers this affordance, so PlanBoardCard itself decides
     * when to render the button; this is only ever invoked for an Enabler card's id. */
    onDeleteItem?: (itemId: string) => void;
    /** True while Ctrl/Cmd is held (see hooks/useModifierKeyActive, PlanPage.tsx's own instance)
     * — forwarded to every placed card as PlanBoardCard's modifierKeyActive prop, so a hovered
     * card shows the pinch cursor before a Ctrl+drag connection actually starts. */
    modifierKeyActive?: boolean;
    /** Id of the connector (from `connectors`) currently selected — rendered in a fixed highlight
     * color, mirroring `selectedLineId`. */
    selectedConnectorId?: string | null;
    /** Fired when a rendered connector is pressed — e.g. to select it for deletion. */
    onConnectorMouseDown?: (connectorId: string, e: React.MouseEvent<SVGPathElement>) => void;
    /** User-entered name labels for the frozen row/column's segments (see PlanAxisLabel,
     * types.ts) — one per region column (frozen row) or row (frozen column). */
    axisLabels?: PlanAxisLabel[];
    /** Fired when a frozen-row/column segment's text input changes. */
    onAxisLabelTextChange?: (orientation: PlanDrawnLine['orientation'], index: number, text: string) => void;
    /** Zoom to start at on mount instead of the computed fit-to-width level — the project's
     * last-used Timeline zoom (see PlanPage.tsx). Omit to always start at the fit-to-width zoom. */
    initialZoom?: number;
    /** Fired with the current zoom every time it changes, so a caller can persist it (see
     * PlanPage.tsx, debounced alongside placements/order/lines/axisLabels). */
    onZoomChange?: (zoom: number) => void;
    /** Chrome floated over the canvas — today PlanTimelineToolbar, pinned to the upper-right
     * corner. Rendered here (rather than in PlanPage's panel header) so it sits within the
     * canvas's own bounds, overlapping the frozen row rather than being confined to the panel's
     * title-bar strip. Forwarded to GridCanvas's `overlay`, which renders it last and outside
     * every pan/zoom-transformed pane, so it neither scrolls nor is clipped by them. Omitted
     * entirely when not passed. */
    cornerMenu?: React.ReactNode;
}

const TimelineCanvas = forwardRef<TimelineCanvasHandle, TimelineCanvasProps>(function TimelineCanvas(
    {
        items, placements, hiddenPlacementId, selectedItemId, onBackgroundClick, onCardMouseDown, onResizeHandleMouseDown,
        dropForbidden, crosshair, lines, selectedLineId, onLineMouseDown,
        connectors, connectorPreview, connectSourceItemId, connectTargetItemId, onCardConnectHover, onCardConnectLeave,
        onDeleteItem, modifierKeyActive, selectedConnectorId, onConnectorMouseDown,
        axisLabels, onAxisLabelTextChange, initialZoom, onZoomChange, cornerMenu,
    },
    ref,
) {
    const itemById = useMemo(() => new Map(items.map((it) => [it.id, it])), [items]);

    // The frozen row/column only make sense once they have something to divide — an empty canvas
    // (or one with only lines of the OTHER orientation) shows neither, so it reads as a plain,
    // undivided canvas rather than pre-emptively reserving chrome nothing has claimed yet.
    // A VERTICAL line splits the canvas into left/right columns, so the frozen ROW (top strip,
    // one label per column) is what a vertical line's existence justifies; a HORIZONTAL line
    // splits it into top/bottom rows, justifying the frozen COLUMN (left strip, one label per
    // row) — see renderFrozenRowSegment/renderFrozenColumnSegment below for the matching
    // "cut by" relationship. Passed straight through as frozenRowGapPx/frozenColumnGapPx, which
    // GridCanvas also uses to drive getFrozenColumnWidth()/getFrozenRowHeight() — so once a pane
    // is hidden this way, PlanPage's drop-rejection/line-clamp guards (resolveFrozenAreaCandidate/
    // resolveLinePosition) relax right along with it, since both read those live handle values.
    const hasVerticalLine = useMemo(() => (lines ?? []).some((line) => line.orientation === 'vertical'), [lines]);
    const hasHorizontalLine = useMemo(() => (lines ?? []).some((line) => line.orientation === 'horizontal'), [lines]);

    // Regions (see PlanRegion, types.ts) are pure geometry derived from the current lines — never
    // stored — recomputed here whenever the line layout changes. Bounded by the grid's own
    // pannable extent, not the raw canvas origin: the frozen row/column are reserved chrome no
    // card (and so no region) can ever occupy, matching resolveLinePosition's own clamp (PlanPage.tsx)
    // that keeps a line's position from ever landing inside them in the first place. `lines` is
    // typed as GridCanvas's generic DrawnLine (it knows nothing about PlanDrawnLine's extra
    // `category` field), but every line actually passed in here IS a PlanDrawnLine — see
    // TimelineCanvasProps.lines and PlanPage's own `lines` state.
    const regions = useMemo(
        () => computeRegions((lines ?? []) as PlanDrawnLine[], {
            xMin: TIMELINE_FROZEN_COLUMN_WIDTH,
            xMax: TIMELINE_GRID_WIDTH,
            yMin: TIMELINE_FROZEN_ROW_HEIGHT,
            yMax: TIMELINE_GRID_HEIGHT,
        }),
        [lines],
    );

    const renderItem = (itemId: string, placement: CanvasRect) => {
        if (itemId === hiddenPlacementId) return null;
        const item = itemById.get(itemId);
        if (!item) return null;
        return (
            <PlanBoardCard
                key={itemId}
                item={item}
                variant='canvas'
                selected={itemId === selectedItemId}
                region={getRegionForPlacement(placement, regions)}
                style={{ left: placement.x, top: placement.y, width: placement.width, height: placement.height }}
                onCardMouseDown={(e) => onCardMouseDown(e, itemId)}
                onResizeHandleMouseDown={(e, edge) => onResizeHandleMouseDown(e, itemId, edge)}
                isConnectionSource={itemId === connectSourceItemId}
                isConnectionTarget={itemId === connectTargetItemId}
                onConnectHoverStart={onCardConnectHover ? () => onCardConnectHover(itemId) : undefined}
                onConnectHoverEnd={onCardConnectLeave}
                connectMode={!!connectSourceItemId}
                modifierKeyActive={modifierKeyActive}
                onDelete={onDeleteItem ? () => onDeleteItem(itemId) : undefined}
            />
        );
    };

    // Frozen-row segments are cut by VERTICAL lines (the priority axis — matches a PlanRegion's
    // columnIndex); frozen-column segments are cut by HORIZONTAL lines (the release axis —
    // matches rowIndex). See GridCanvas's own renderFrozenRowSegment/renderFrozenColumnSegment
    // doc comments for the geometry; here we only need to look up (and write back) each
    // segment's PlanAxisLabel.
    const renderFrozenRowSegment = (index: number, min: number, _max: number, visibleMin: number, visibleMax: number) => (
        <PlanAxisLabelCell
            key={index}
            vertical={false}
            label={findAxisLabel(axisLabels ?? [], 'vertical', index)}
            min={min}
            visibleMin={visibleMin}
            visibleMax={visibleMax}
            onTextChange={(text) => onAxisLabelTextChange?.('vertical', index, text)}
        />
    );
    const renderFrozenColumnSegment = (index: number, min: number, _max: number, visibleMin: number, visibleMax: number) => (
        <PlanAxisLabelCell
            key={index}
            vertical
            label={findAxisLabel(axisLabels ?? [], 'horizontal', index)}
            min={min}
            visibleMin={visibleMin}
            visibleMax={visibleMax}
            onTextChange={(text) => onAxisLabelTextChange?.('horizontal', index, text)}
        />
    );

    return (
        <GridCanvas
            ref={ref}
            placements={placements}
            renderItem={renderItem}
            onBackgroundClick={onBackgroundClick}
            dropForbidden={dropForbidden}
            crosshair={crosshair}
            lines={lines}
            lineThickness={TIMELINE_DRAWN_LINE_THICKNESS}
            selectedLineId={selectedLineId}
            onLineMouseDown={onLineMouseDown}
            connectors={connectors}
            connectorPreview={connectorPreview}
            selectedConnectorId={selectedConnectorId}
            onConnectorMouseDown={onConnectorMouseDown}
            renderFrozenRowSegment={renderFrozenRowSegment}
            renderFrozenColumnSegment={renderFrozenColumnSegment}
            overlay={cornerMenu}
            classPrefix='timeline-canvas'
            gridSpacing={TIMELINE_GRID_SPACING}
            minorDivisions={TIMELINE_MINOR_DIVISIONS}
            horizontalColumns={TIMELINE_HORIZONTAL_COLUMNS}
            visibleColumns={TIMELINE_VISIBLE_COLUMNS}
            verticalExtent={TIMELINE_GRID_HEIGHT}
            maxZoom={TIMELINE_MAX_ZOOM}
            initialZoom={initialZoom}
            onZoomChange={onZoomChange}
            minSnapWidth={PLAN_CARD_MIN_WIDTH}
            minSnapHeight={PLAN_CARD_MIN_HEIGHT}
            frozenColumnGapPx={hasHorizontalLine ? TIMELINE_FROZEN_GAP_PX : 0}
            frozenRowGapPx={hasVerticalLine ? TIMELINE_FROZEN_GAP_PX : 0}
            backgroundColor={CANVAS_THEME.background}
            gridStyle={CANVAS_THEME.gridStyle}
            gridLineColor={CANVAS_THEME.gridColor}
            gridLineOpacity={CANVAS_THEME.gridOpacity}
            gridDotRadius={CANVAS_THEME.gridDotRadius}
            gridDotSpacing={CANVAS_THEME.gridDotSpacing}
            dividerColor={CANVAS_THEME.dividerColor}
            dividerWidth={CANVAS_THEME.dividerWidth}
        />
    );
});

export default TimelineCanvas;
